import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Copy, Trash2, Loader2, Film } from "lucide-react";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { AdminTitle, Spinner } from "@/admin/AdminLayout";
import AuditLog from "@/admin/hr/AuditLog";

export function MediaPage() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/media").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);
  const upload = async (files) => {
    setBusy(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post("/admin/media", fd);
      } catch (e) {
        toast.error(`${f.name}: ${errMsg(e)}`);
      }
    }
    setBusy(false);
    load();
  };
  const del = async (m) => {
    if (!window.confirm("Bu dosya kaldırılsın mı? Kullanıldığı sayfalarda görünmez olur.")) return;
    await api.delete(`/admin/media/${m.id}`);
    load();
  };
  return (
    <div>
      <AdminTitle title="Medya Kütüphanesi" desc="Görseller otomatik olarak WebP biçimine dönüştürülür. Bir dosyanın adresini kopyalayıp içerik alanlarına yapıştırabilirsiniz." />
      <label className="mb-6 flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-zinc-300 bg-white p-8 text-sm font-semibold text-zinc-600 hover:border-[color:var(--red)]" data-testid="media-upload-zone">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />} Görsel veya MP4 video yükle
        <input type="file" multiple accept="image/*,video/mp4" className="sr-only" onChange={(e) => upload([...e.target.files])} data-testid="media-upload-input" />
      </label>
      {!items ? <Spinner /> : items.length === 0 ? <p className="text-sm text-zinc-500">Henüz yüklenmiş dosya yok. Sitedeki varsayılan görseller uygulama içinde yer alır.</p> : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="media-grid">
          {items.map((m) => (
            <div key={m.id} className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
              <div className="flex aspect-square items-center justify-center bg-zinc-100">
                {m.content_type.startsWith("image") ? <img src={mediaUrl(m.url)} alt={m.filename} className="h-full w-full object-cover" loading="lazy" /> : <Film className="h-10 w-10 text-zinc-400" />}
              </div>
              <div className="flex items-center justify-between gap-1 p-2">
                <span className="truncate text-xs text-zinc-600">{m.filename}</span>
                <span className="flex shrink-0">
                  <button onClick={() => { navigator.clipboard.writeText(m.url); toast.success("Adres kopyalandı"); }} className="rounded p-1.5 hover:bg-zinc-100" aria-label="Adresi kopyala"><Copy className="h-3.5 w-3.5" /></button>
                  <button onClick={() => del(m)} className="rounded p-1.5 text-red-600 hover:bg-red-50" aria-label="Sil"><Trash2 className="h-3.5 w-3.5" /></button>
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Account() {
  const [f, setF] = useState({ current_password: "", new_password: "", again: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password.length < 10) return toast.error("Yeni şifre en az 10 karakter olmalıdır.");
    if (f.new_password !== f.again) return toast.error("Yeni şifreler eşleşmiyor.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Şifreniz değiştirildi");
      setF({ current_password: "", new_password: "", again: "" });
    } catch (ex) {
      toast.error(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };
  const inp = (k, l) => <div><label className="field-label">{l}</label><input type="password" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="field-input" data-testid={`account-${k}`} /></div>;
  return (
    <div>
      <AdminTitle title="Yönetici Hesabı ve Güvenlik" desc="Şifrenizi değiştirin ve paneldeki önemli işlemlerin denetim kayıtlarını inceleyin. Herkese açık kayıt sayfası yoktur; yalnızca yetkili yönetici giriş yapabilir." />
      <form onSubmit={submit} className="max-w-md space-y-4 rounded-lg border border-zinc-200 bg-white p-5" data-testid="change-password-form">
        {inp("current_password", "Mevcut şifre")}
        {inp("new_password", "Yeni şifre (en az 10 karakter)")}
        {inp("again", "Yeni şifre (tekrar)")}
        <button disabled={busy} className="btn-red" data-testid="change-password-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Şifreyi Değiştir</button>
      </form>
      <div className="mt-6"><AuditLog /></div>
    </div>
  );
}
